// useAdminSession.ts — Canister-issued session token lifecycle for the admin console.
//
// Worker auth via canister-issued session token (useAdminSession). The shared-secret
// ADMIN_API_TOKEN path is retained on the Worker side for non-interactive callers
// (cron, scripts).
//
// Token is kept in React state ONLY — never persisted to localStorage or
// sessionStorage, as these are credentials. On page refresh, the user gets a
// fresh token automatically (II login is already persisted via @dfinity/auth-client;
// the new token is auto-issued on mount once identity is non-null).
//
// Auto-refresh: a setTimeout fires 5 minutes before expiresAt so the operator
// never hits an expired-token mid-workflow. Timer is cleared on unmount to
// prevent leaks.
//
// Conversion: canister expiresAt is bigint nanoseconds since Unix epoch.
//   JS milliseconds = Number(expiresAt / 1_000_000n)
// The division is done in bigint to avoid precision loss before the Number cast.
//
// TODO (unit tests): The admin workspace has no test runner (no vitest/jest in
// sites/admin/package.json). When a test runner is added, cover:
//   1. Mount with non-null identity → calls admin_issue_session_token
//   2. Mount with null identity → no call, isReady=false
//   3. Auto-refresh timer fires 5 min before expiry
//   4. refresh() explicit call re-issues token
//   5. revoke() clears state + calls admin_revoke_session_token
//   6. Unmount clears pending refresh timer

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import React from 'react';
import type { Identity } from '@dfinity/agent';
import { createAdminAgent, createAdminActor } from '../lib/adminAgent';

// ---------------------------------------------------------------------------
// Five minutes in milliseconds — refresh window before expiry
// ---------------------------------------------------------------------------

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseAdminSessionReturn {
  /** 64-char lowercase hex token, or null before issue completes or on error. */
  token: string | null;
  /** JS milliseconds since epoch (converted from bigint nanoseconds), or null. */
  expiresAt: number | null;
  isLoading: boolean;
  error: string | null;
  /** Convenience: token !== null && expiresAt !== null && expiresAt > Date.now() */
  isReady: boolean;
  /** Explicit re-issue. Called by auto-refresh timer and by error-retry paths. */
  refresh: () => Promise<void>;
  /** Revoke current token via canister, clear state. Called before II logout. */
  revoke: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useAdminSession(identity: Identity | null): UseAdminSessionReturn {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Core issue call — shared by initial mount and refresh()
  // -------------------------------------------------------------------------

  const issueToken = useCallback(async () => {
    if (!identity) return;

    setIsLoading(true);
    setError(null);
    clearRefreshTimer();

    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_issue_session_token();

      if (!isMountedRef.current) return;

      if ('err' in result) {
        setError(result.err);
        return;
      }

      const newToken = result.ok.token;
      // Convert bigint nanoseconds to JS milliseconds without precision loss.
      const expiresAtMs = Number(result.ok.expiresAt / 1_000_000n);

      setToken(newToken);
      setExpiresAt(expiresAtMs);

      // Schedule an auto-refresh 5 minutes before expiry.
      const msUntilRefresh = expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
      if (msUntilRefresh > 0) {
        refreshTimerRef.current = setTimeout(() => {
          // issueToken captures identity in closure; identity is stable per mount cycle.
          issueToken();
        }, msUntilRefresh);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const msg = err instanceof Error ? err.message : 'Failed to issue admin session token.';
      setError(msg);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [identity, clearRefreshTimer]);

  // -------------------------------------------------------------------------
  // Public refresh — explicit re-issue
  // -------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    await issueToken();
  }, [issueToken]);

  // -------------------------------------------------------------------------
  // Revoke — clears state and tells the canister to invalidate the token.
  // Called before II logout. Proceed on failure (token expires on its own).
  // -------------------------------------------------------------------------

  const revoke = useCallback(async () => {
    const currentToken = token;
    // Clear state immediately — no need to wait for the canister call.
    setToken(null);
    setExpiresAt(null);
    clearRefreshTimer();

    if (!currentToken || !identity) return;

    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      await actor.admin_revoke_session_token(currentToken);
    } catch (err) {
      // Canister unreachable — token will expire on its own. Non-fatal.
      console.error('[useAdminSession] revoke failed (token will self-expire):', err);
    }
  }, [token, identity, clearRefreshTimer]);

  // -------------------------------------------------------------------------
  // Mount effect — issue token as soon as identity is available
  // -------------------------------------------------------------------------

  useEffect(() => {
    isMountedRef.current = true;

    if (identity) {
      issueToken();
    }

    return () => {
      isMountedRef.current = false;
      clearRefreshTimer();
    };
  }, [identity, issueToken, clearRefreshTimer]);

  const isReady = token !== null && expiresAt !== null && expiresAt > Date.now();

  return {
    token,
    expiresAt,
    isLoading,
    error,
    isReady,
    refresh,
    revoke,
  };
}

// ---------------------------------------------------------------------------
// Context — allows any admin component to consume the session without
// prop-drilling. Matches the AuthProvider pattern in src/hooks/AuthProvider.tsx.
// ---------------------------------------------------------------------------

const AdminSessionContext = createContext<UseAdminSessionReturn | null>(null);

export interface AdminSessionProviderProps {
  identity: Identity | null;
  children: React.ReactNode;
}

export function AdminSessionProvider({
  identity,
  children,
}: AdminSessionProviderProps): React.ReactElement {
  const session = useAdminSession(identity);

  // useMemo keeps the context value reference stable when session values
  // haven't changed — avoids unnecessary re-renders downstream.
  const value = useMemo(() => session, [
    session.token,
    session.expiresAt,
    session.isLoading,
    session.error,
    session.isReady,
    session.refresh,
    session.revoke,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  return React.createElement(
    AdminSessionContext.Provider,
    { value },
    children,
  );
}

/**
 * Consume the session context. Must be used inside AdminSessionProvider.
 * Throws if called outside the provider so callers fail fast in development.
 */
export function useAdminSessionContext(): UseAdminSessionReturn {
  const ctx = useContext(AdminSessionContext);
  if (ctx === null) {
    throw new Error('[useAdminSessionContext] Must be used inside <AdminSessionProvider>.');
  }
  return ctx;
}
